<script setup lang="ts">
import type { FieldObservation } from "@/types/field-observations.types";
import { useDates } from "@/composable/useDates";
import { useTranslation } from "i18next-vue";
import { computed, ref } from "vue";
import APIClient from "@/APIClient";
import PhosphorIcon from "@/components/UI/PhosphorIcon.vue";
import { getObservationTypeConfig } from "./observation-type";

const { formatDistanceToNow } = useDates();
const { t } = useTranslation();

const props = defineProps<{
  observation: FieldObservation;
}>();

defineEmits<{
  (e: "select"): void;
}>();

// Photo thumbnails
const photoThumbnails = ref<Map<number, string>>(new Map());
const loadingPhotos = ref<Set<number>>(new Set());

// Audio recordings URLs
const audioUrls = ref<Map<number, string>>(new Map());
const loadingAudios = ref<Set<number>>(new Set());

const loadPhotoThumbnail = async (fileId: number) => {
  if (photoThumbnails.value.has(fileId) || loadingPhotos.value.has(fileId))
    return;

  loadingPhotos.value.add(fileId);
  try {
    const result = await APIClient.getFileUrl(fileId);
    photoThumbnails.value.set(fileId, result.url);
  } catch (error) {
    console.error("Failed to load photo thumbnail:", error);
  } finally {
    loadingPhotos.value.delete(fileId);
  }
};

// Load thumbnails for first 3 photos
const visiblePhotos = computed(() => {
  const photos = props.observation.photos?.slice(0, 3) || [];
  photos.forEach((photo) => {
    if (photo.fileId) {
      loadPhotoThumbnail(photo.fileId);
    }
  });
  return photos;
});

const extraPhotosCount = computed(() => {
  const total = props.observation.photos?.length || 0;
  return total > 3 ? total - 3 : 0;
});

const typeConfig = computed(() => {
  const config = getObservationTypeConfig(props.observation.type);
  return {
    icon: config.icon,
    color: config.color,
    label: config.labelKey ? t(config.labelKey) : "",
  };
});

const loadAudioUrl = async (fileId: number) => {
  if (audioUrls.value.has(fileId) || loadingAudios.value.has(fileId)) return;

  loadingAudios.value.add(fileId);
  try {
    const result = await APIClient.getFileUrl(fileId);
    audioUrls.value.set(fileId, result.url);
  } catch (error) {
    console.error("Failed to load audio URL:", error);
  } finally {
    loadingAudios.value.delete(fileId);
  }
};

const hasAudioRecordings = computed(
  () => (props.observation.audioRecordings?.length || 0) > 0,
);

// Load audio URLs when needed
const visibleAudios = computed(() => {
  const audios = props.observation.audioRecordings || [];
  audios.forEach((audio) => {
    if (audio.fileId) {
      loadAudioUrl(audio.fileId);
    }
  });
  return audios;
});
const hasPhotos = computed(() => (props.observation.photos?.length || 0) > 0);

const authorName = computed(
  () => props.observation.author?.full_name ?? "Inconnu",
);
const authorInitials = computed(() => {
  const name = authorName.value;
  const parts = name.split(" ");
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
});
</script>

<template>
  <div class="observation-item" @click="$emit('select')">
    <!-- Left: Type icon -->
    <div
      class="observation-avatar"
      :style="{
        backgroundColor: typeConfig.color + '20',
        color: typeConfig.color,
      }"
    >
      <PhosphorIcon :name="typeConfig.icon" :size="20" weight="bold" />
    </div>

    <!-- Center: Content -->
    <div class="observation-content">
      <!-- Line 1: Type + Part badge -->
      <div class="observation-header">
        <span class="observation-type" :style="{ color: typeConfig.color }">
          {{ typeConfig.label }}
        </span>
        <span v-if="observation.part" class="observation-part-badge">
          {{ observation.part.name }}
        </span>
      </div>

      <!-- Description (3 lines max) -->
      <div v-if="observation.description" class="observation-description">
        {{ observation.description }}
      </div>

      <!-- Photo thumbnails -->
      <div v-if="hasPhotos" class="photo-thumbnails">
        <div v-for="photo in visiblePhotos" :key="photo.id" class="photo-thumb">
          <img
            v-if="photoThumbnails.get(photo.fileId)"
            :src="photoThumbnails.get(photo.fileId)"
            alt=""
          />
          <div v-else class="photo-loading">
            <span class="spinner-border spinner-border-sm" />
          </div>
        </div>
        <div v-if="extraPhotosCount > 0" class="photo-more">
          +{{ extraPhotosCount }}
        </div>
      </div>

      <!-- Audio recordings (stacked) -->
      <div v-if="hasAudioRecordings" class="audio-recordings" @click.stop>
        <div v-for="audio in visibleAudios" :key="audio.id" class="audio-item">
          <div v-if="loadingAudios.has(audio.fileId)" class="audio-loading">
            <span class="spinner-border spinner-border-sm" />
          </div>
          <audio
            v-else-if="audioUrls.get(audio.fileId)"
            :src="audioUrls.get(audio.fileId)"
            class="audio-player"
            controls
          />
        </div>
      </div>

      <!-- Meta: Author + Time -->
      <div class="observation-meta">
        <span class="author-avatar">{{ authorInitials }}</span>
        <span class="author-name">{{ authorName }}</span>
        <span class="observation-time">
          {{ formatDistanceToNow(observation.createdAt) }}
        </span>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.observation-item {
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px;
  background-color: var(--el-bg-color);
  border-radius: calc(var(--bs-border-radius) * 2);
  cursor: pointer;
  transition: background-color 0.15s ease;

  &:hover {
    background-color: var(--bs-tertiary-bg);
  }
}

.observation-avatar {
  width: 40px;
  height: 40px;
  min-width: 40px;
  border-radius: calc(var(--bs-border-radius) * 2);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
}

.observation-content {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.observation-header {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.observation-type {
  font-weight: 600;
  font-size: 14px;
  white-space: nowrap;
}

.observation-part-badge {
  font-size: 11px;
  padding: 2px 8px;
  background-color: var(--bs-secondary-bg);
  color: var(--bs-secondary-color);
  border-radius: var(--bs-border-radius);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 150px;
}

.observation-description {
  font-size: 14px;
  color: var(--bs-body-color);
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.photo-thumbnails {
  display: flex;
  gap: 6px;
  align-items: center;
}

.photo-thumb {
  width: 48px;
  height: 48px;
  border-radius: 8px;
  overflow: hidden;
  background-color: var(--bs-secondary-bg);
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.photo-loading {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--bs-secondary-color);

  .spinner-border {
    width: 16px;
    height: 16px;
    border-width: 2px;
  }
}

.photo-more {
  width: 48px;
  height: 48px;
  border-radius: 8px;
  background-color: var(--bs-secondary-bg);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 600;
  color: var(--bs-secondary-color);
}

.audio-recordings {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.audio-item {
  border-radius: 8px;

  .audio-loading {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 28px;
    color: var(--bs-secondary-color);
  }

  .audio-player {
    width: 100%;
    height: 28px;
  }
}

.observation-meta {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: var(--bs-secondary-color);
}

.author-avatar {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background-color: var(--bs-primary);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 9px;
  font-weight: 600;
}

.author-name {
  font-weight: 500;
}

.observation-time {
  color: var(--bs-tertiary-color);
}
</style>
